import { JSX } from 'react';

import { getInventoryRulesFilters } from '~/src/dash/apiV2/inventory-rules/inventoryRules';
import { convertSetIntoArrayOfStrings } from '~/src/dash/components/filters/conversions';
import { IExtendedIFilterRowObject } from '~/src/dash/components/filters/FilterRowV2';
import {
  ICustomRenderCellJSXProps,
  IListViewTableColumns,
  IListViewTablePagination,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { spacing } from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import {
  InventoryRulesModalLink,
  NameWithDefault,
} from '~/src/dash/views/inventory-rules-v2/common';

export interface IContentMetadataListConfig {
  cypressClassName: string;
  defaultState: {
    activeColumnKeys: string[];
    orderDirection: Order;
    orderBy: TContentMetadataListKey;
    page: number;
    rowsPerPage: RowsPerPage;
  };
  timezone: string;
  filters: Record<string, IExtendedIFilterRowObject>;
  columns: IListViewTableColumns;
  table: IListViewTablePagination;
  activeColumnKeys: string[];
  columnSelector: {
    resetKeys: string[];
  };
}

export const contentMetadataListConfig: IContentMetadataListConfig = {
  cypressClassName: 'content-metadata-list',
  defaultState: {
    activeColumnKeys: [
      'AssociatedInventoryRules',
      'ContentGenre',
      'ContentRating',
      'ContentCategory',
      'ContentNetwork',
      'ContentChannel',
      'ContentLanguage',
    ],
    orderDirection: 'desc',
    orderBy: 'ID',
    page: 0,
    rowsPerPage: 25,
  },
  timezone: 'UTC',
  activeColumnKeys: [
    'AssociatedInventoryRules',
    'ContentGenre',
    'ContentRating',
    'ContentCategory',
    'ContentNetwork',
    'ContentChannel',
    'ContentLanguage',
  ],
  filters: {
    ContentCategory: {
      key: 'ContentCategory',
      tKey: 'ContentCategory',
      getList: (session: ISessionState) =>
        getInventoryRulesFilters(session.publisher.ID).then((data) => data.ContentCategory || []),
      selectedLimit: 0,
      queryKey: 'ContentCategory',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
    ContentGenre: {
      key: 'ContentGenre',
      tKey: 'ContentGenre',
      getList: (session: ISessionState) =>
        getInventoryRulesFilters(session.publisher.ID).then((data) => data.ContentGenre || []),
      selectedLimit: 0,
      queryKey: 'ContentGenre',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
    ContentNetwork: {
      key: 'ContentNetwork',
      tKey: 'ContentNetwork',
      getList: (session: ISessionState) =>
        getInventoryRulesFilters(session.publisher.ID).then((data) => data.ContentNetwork || []),
      selectedLimit: 0,
      queryKey: 'ContentNetwork',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
    ContentTitle: {
      key: 'ContentTitle',
      tKey: 'ContentTitle',
      getList: (session: ISessionState) =>
        getInventoryRulesFilters(session.publisher.ID).then((data) => data.ContentTitle || []),
      selectedLimit: 0,
      queryKey: 'ContentTitle',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
  },
  columns: {
    ID: {
      formatValue: (value: number) => value.toString(),
      isHidden: true,
      isSearchable: false,
      key: 'ID',
      label: 'ID',
      requestKey: 'ID',
      responseKey: 'ID',
      valueType: 'string',
    },
    Name: {
      cellJSX: (props: ICustomRenderCellJSXProps): JSX.Element =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
        NameWithDefault({ ...props, fallbackName: 'UnnamedContentMetadata' }),
      cellRenderType: 'name',
      defaultOrderDirection: 'asc',
      headerRenderType: 'name',
      headerStyle: {
        width: '40%',
      },
      cellStyle: {
        width: '40%',
      },
      isSearchable: true,
      key: 'Name',
      label: 'Name',
      tKey: 'Name',
      requestKey: 'Name',
      responseKey: 'Name',
      valueType: 'string',
      orderByKey: 'Name',
    },
    AssociatedInventoryRules: {
      cellJSX: InventoryRulesModalLink,
      headerRenderType: 'number',
      cellRenderType: 'number',
      headerStyle: {
        textAlign: 'left',
        width: '40%',
        paddingLeft: spacing.none,
      },
      cellStyle: {
        textAlign: 'left',
        width: '40%',
        paddingLeft: spacing.none,
      },
      key: 'AssociatedInventoryRules',
      label: 'Associated Inventory Rules',
      requestKey: 'AssociatedInventoryRules',
      responseKey: 'AssociatedInventoryRules',
      valueType: 'number',
    },
    ContentTitle: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentTitle',
      tKey: 'ContentTitle',
      label: 'Content Title',
      requestKey: '',
      responseKey: 'ContentTitle',
      valueType: 'string',
      orderByKey: 'ContentTitle',
    },
    ContentGenre: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentGenre',
      tKey: 'ContentGenre',
      label: 'Content Genre',
      requestKey: '',
      responseKey: 'ContentGenre',
      valueType: 'string',
      orderByKey: 'ContentGenre',
    },
    ContentNetwork: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentNetwork',
      tKey: 'ContentNetwork',
      label: 'Content Network',
      requestKey: '',
      responseKey: 'ContentNetwork',
      valueType: 'string',
      orderByKey: 'ContentNetwork',
    },
    ContentCategory: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentCategory',
      tKey: 'ContentCategory',
      label: 'Content Category',
      requestKey: '',
      responseKey: 'ContentCategory',
      valueType: 'string',
      orderByKey: 'ContentCategory',
    },
    ContentID: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentID',
      tKey: 'ContentID',
      label: 'Content ID',
      requestKey: '',
      responseKey: 'ContentID',
      valueType: 'string',
      orderByKey: 'ContentID',
    },
    ContentChannel: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentChannel',
      tKey: 'ContentChannel',
      label: 'Content Channel',
      requestKey: '',
      responseKey: 'ContentChannel',
      valueType: 'string',
      orderByKey: 'ContentChannel',
    },
    ContentContext: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentContext',
      tKey: 'ContentContext',
      label: 'Content Context',
      requestKey: '',
      responseKey: 'ContentContext',
      valueType: 'string',
      orderByKey: 'ContentContext',
    },
    ContentDistributionID: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentDistributionID',
      tKey: 'ContentDistributionID',
      label: 'Content Distribution ID',
      requestKey: '',
      responseKey: 'ContentDistributionID',
      valueType: 'string',
      orderByKey: 'ContentDistributionID',
    },
    ContentDistributionName: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentDistributionName',
      tKey: 'ContentDistributionName',
      label: 'Content Distribution Name',
      requestKey: '',
      responseKey: 'ContentDistributionName',
      valueType: 'string',
      orderByKey: 'ContentDistributionName',
    },
    ContentEpisode: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentEpisode',
      tKey: 'ContentEpisode',
      label: 'Content Episode',
      requestKey: '',
      responseKey: 'ContentEpisode',
      valueType: 'string',
      orderByKey: 'ContentEpisode',
    },
    ContentKeywords: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentKeywords',
      tKey: 'ContentKeywords',
      label: 'Content Keywords',
      requestKey: '',
      responseKey: 'ContentKeywords',
      valueType: 'string',
      orderByKey: 'ContentKeywords',
    },
    ContentLanguage: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentLanguage',
      tKey: 'ContentLanguage',
      label: 'Content Language',
      requestKey: '',
      responseKey: 'ContentLanguage',
      valueType: 'string',
      orderByKey: 'ContentLanguage',
    },
    ContentLength: {
      headerRenderType: 'number',
      cellRenderType: 'number',
      key: 'ContentLength',
      tKey: 'ContentLength',
      label: 'Content Length',
      requestKey: '',
      responseKey: 'ContentLength',
      valueType: 'number',
      orderByKey: 'ContentLength',
    },
    ContentProdQuality: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentProdQuality',
      tKey: 'ContentProdQuality',
      label: 'Content Prod Quality',
      requestKey: '',
      responseKey: 'ContentProdQuality',
      valueType: 'string',
      orderByKey: 'ContentProdQuality',
    },
    ContentProducerID: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentProducerID',
      tKey: 'ContentProducerID',
      label: 'Content Producer ID',
      requestKey: '',
      responseKey: 'ContentProducerID',
      valueType: 'string',
      orderByKey: 'ContentProducerID',
    },
    ContentProducer: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentProducer',
      tKey: 'ContentProducer',
      label: 'Content Producer',
      requestKey: '',
      responseKey: 'ContentProducer',
      valueType: 'string',
      orderByKey: 'ContentProducer',
    },
    ContentRating: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentRating',
      tKey: 'ContentRating',
      label: 'Content Rating',
      requestKey: '',
      responseKey: 'ContentRating',
      valueType: 'string',
      orderByKey: 'ContentRating',
    },
    ContentSeason: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentSeason',
      tKey: 'ContentSeason',
      label: 'Content Season',
      requestKey: '',
      responseKey: 'ContentSeason',
      valueType: 'string',
      orderByKey: 'ContentSeason',
    },
    ContentSeries: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentSeries',
      tKey: 'ContentSeries',
      label: 'Content Series',
      requestKey: '',
      responseKey: 'ContentSeries',
      valueType: 'string',
      orderByKey: 'ContentSeries',
    },
    ContentTaxonomy: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentTaxonomy',
      tKey: 'ContentTaxonomy',
      label: 'Content Taxonomy',
      requestKey: '',
      responseKey: 'ContentTaxonomy',
      valueType: 'string',
      orderByKey: 'ContentTaxonomy',
    },
    ContentURL: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentURL',
      tKey: 'ContentURL',
      label: 'Content URL',
      requestKey: '',
      responseKey: 'ContentURL',
      valueType: 'string',
      orderByKey: 'ContentURL',
    },
  },
  columnSelector: {
    resetKeys: [
      'AssociatedInventoryRules',
      'ContentGenre',
      'ContentRating',
      'ContentCategory',
      'ContentNetwork',
      'ContentChannel',
      'ContentLanguage',
    ],
  },
  table: {
    order: 'desc',
    orderBy: 'ID',
    page: 0,
    rowsPerPage: 25,
  },
} as unknown as IContentMetadataListConfig;

export type TContentMetadataListKey = keyof typeof contentMetadataListConfig.columns;
